import { Button, Stack, TextField, Typography } from '@mui/material';
import { type ChangeEvent, memo, type ReactNode } from 'react';
import { type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { DynamicIcon } from 'src/components';
import { FormAutocomplete, FormSwitch, FormTextField } from 'src/components/Forms';
import {
  NO_PLATFORM_SLA_OPTION,
  resolvePlatformSlaId,
} from 'src/pages/Settings/memberships/components/plans-tab/form-values';
import {
  PlanAccordionDetails,
  PlanAccordionSummary,
  PlanAccordionWrapper,
  planControlFormSx,
  planHeaderToggleFormSx,
  planHeaderToggleLabelSx,
  PlanName,
  planToggleLabelSx,
  planToggleSx,
} from 'src/pages/Settings/memberships/components/plans-tab/styled';
import {
  PlansTabField,
  PlansTabFormValues,
  PlansTabPlanValues,
  PlatformSlaFieldState,
} from 'src/pages/Settings/memberships/components/plans-tab/types';
import PlanPriceField from './plan-price-field';

const PLAN_NAME_MAX_LENGTH = 50;

const removePlanButtonSx = {
  alignSelf: 'flex-start',
  textTransform: 'none',
  fontWeight: 500,
  mt: 2,
} as const;

interface PlanAccordionProps {
  index: number;
  planId: string;
  expanded: boolean;
  onExpandedChange: (planId: string, expanded: boolean) => void;
  onRemove: (index: number, planId: string) => void;
  autoFocusName: boolean;
  isNew: boolean;
  platformSla: PlatformSlaFieldState;
  readOnly: boolean;
}

type PlanFieldPath = FieldPath<PlansTabFormValues>;

const getPlanFieldName = (index: number, field: keyof PlansTabPlanValues): PlanFieldPath =>
  `${PlansTabField.Plans}.${index}.${field}` as PlanFieldPath;

interface PlanSwitchFieldProps {
  name: string;
  label: ReactNode;
  ariaLabel: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  sx?: Parameters<typeof FormSwitch>[0]['sx'];
  labelSx?: Parameters<typeof FormSwitch>[0]['labelSx'];
  textSx?: Parameters<typeof Typography>[0]['sx'];
}

const PlanSwitchField = ({
  name,
  label,
  ariaLabel,
  onChange,
  disabled = false,
  sx = planControlFormSx,
  labelSx = planToggleLabelSx,
  textSx = planToggleSx,
}: PlanSwitchFieldProps): React.JSX.Element => (
  <FormSwitch
    type="switch"
    name={name}
    label={<Typography sx={textSx}>{label}</Typography>}
    labelPlacement="start"
    sx={sx}
    labelSx={labelSx}
    switchFieldProps={{
      size: 'small',
      sx: planToggleSx,
      inputProps: { 'aria-label': ariaLabel },
    }}
    onChange={onChange}
    disabled={disabled}
  />
);

export const PlanAccordion = ({
  index,
  planId,
  expanded,
  onExpandedChange,
  onRemove,
  autoFocusName,
  isNew,
  platformSla,
  readOnly,
}: PlanAccordionProps): React.JSX.Element => {
  const { control, getValues, setValue } = useFormContext<PlansTabFormValues>();
  const {
    hasAccess: hasServiceLevelAccess,
    loading: slaOptionsLoading,
    options: platformSlaOptions,
  } = platformSla;

  const planValues = useWatch({
    control,
    name: `${PlansTabField.Plans}.${index}`,
  }) as PlansTabPlanValues | undefined;

  const billingOptions = useWatch({
    control,
    name: PlansTabField.BillingOptions,
  }) as PlansTabFormValues['billingOptions'] | undefined;

  // A saved plan's SLA is shown read-only: it is set at creation and there is no update path.
  const currentSlaId =
    resolvePlatformSlaId(planValues?.platformSlaId) ?? NO_PLATFORM_SLA_OPTION.id;
  const currentSlaLabel =
    platformSlaOptions.find((option) => option.id === currentSlaId)?.label ??
    NO_PLATFORM_SLA_OPTION.label;

  const planName = planValues?.name ?? 'Plan';
  const isEnabled = planValues?.enabled ?? false;
  const monthlyEnabled = billingOptions?.monthlyEnabled ?? false;
  const annualEnabled = billingOptions?.annualEnabled ?? false;
  const callForPricing = planValues?.callForPricing ?? false;
  const showPriceFields = isEnabled && (isNew || !callForPricing);

  const handleMostPopularChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (readOnly) {
      return;
    }

    if (!event.target.checked) {
      return;
    }

    const plans = getValues(PlansTabField.Plans);

    if (!Array.isArray(plans)) {
      return;
    }

    plans.forEach((plan, planIndex) => {
      if (planIndex === index || !plan?.mostPopular) {
        return;
      }

      setValue(getPlanFieldName(planIndex, 'mostPopular'), false, {
        shouldDirty: true,
        shouldTouch: true,
      });
    });
  };

  return (
    <PlanAccordionWrapper
      elevation={0}
      disableGutters
      expanded={isNew || (isEnabled && expanded)}
      onChange={(_, expandedValue) => {
        if (isNew || !isEnabled) {
          return;
        }
        onExpandedChange(planId, expandedValue);
      }}
      data-testid={`plan-accordion-${planId}`}
    >
      <PlanAccordionSummary
        data-disabled={!isEnabled || isNew}
        expandIcon={
          isEnabled && !isNew ? <DynamicIcon name="chevron-right" library="lucide" /> : null
        }
      >
        <PlanName
          variant="body2"
          sx={{
            color: isEnabled ? 'text.primary' : 'text.disabled',
          }}
        >
          {planName}
        </PlanName>
        <Stack
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <PlanSwitchField
            name={getPlanFieldName(index, 'enabled')}
            label=""
            ariaLabel="Plan active toggle"
            disabled={readOnly || isNew}
            onChange={(event) => {
              event.stopPropagation();
            }}
            sx={planHeaderToggleFormSx}
            labelSx={planHeaderToggleLabelSx}
          />
        </Stack>
        {/* commented for future use
         <Stack direction="row" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            aria-label="Move plan up"
            disabled={!onMoveUp}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onMoveUp?.();
            }}
          >
            <ArrowUpwardIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Move plan down"
            disabled={!onMoveDown}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onMoveDown?.();
            }}
          >
            <ArrowDownwardIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Delete plan"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }}
          >
            <DeleteOutlineIcon fontSize="inherit" />
          </IconButton>
        </Stack> */}
      </PlanAccordionSummary>

      {isNew || (isEnabled && expanded) ? (
        <PlanAccordionDetails>
          <Stack>
            {isNew ? (
              <FormTextField
                type="textfield"
                name={getPlanFieldName(index, 'name')}
                label="Plan name"
                required="Plan name is required"
                pattern={{ value: /\S/, message: 'Plan name is required' }}
                disabled={readOnly}
                textFieldProps={{
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  inputProps: { maxLength: PLAN_NAME_MAX_LENGTH },
                  autoFocus: autoFocusName,
                  onFocus: autoFocusName
                    ? (event) => {
                        event.target.select();
                      }
                    : undefined,
                }}
              />
            ) : null}

            <PlanSwitchField
              name={getPlanFieldName(index, 'mostPopular')}
              label="Most popular"
              ariaLabel="Most popular toggle"
              onChange={handleMostPopularChange}
              disabled={readOnly}
            />

            <PlanSwitchField
              name={getPlanFieldName(index, 'callForPricing')}
              label="Call for pricing"
              ariaLabel="Call for pricing toggle"
              disabled={readOnly}
            />

            {showPriceFields ? (
              <Stack gap={2} width="100%" mt={2}>
                {monthlyEnabled ? (
                  <PlanPriceField
                    name={getPlanFieldName(index, 'monthlyPrice')}
                    label="Monthly price"
                    endAdornment="/mo"
                    isRequired={monthlyEnabled}
                    disabled={readOnly}
                  />
                ) : null}

                {annualEnabled ? (
                  <PlanPriceField
                    name={getPlanFieldName(index, 'annualPrice')}
                    label="Annual price"
                    endAdornment="/yr"
                    isRequired={annualEnabled}
                    disabled={readOnly}
                  />
                ) : null}
              </Stack>
            ) : null}

            {hasServiceLevelAccess ? (
              <Stack width="100%" mt={2}>
                {isNew ? (
                  <FormAutocomplete
                    type="autocomplete"
                    name={getPlanFieldName(index, 'platformSlaId')}
                    label="OneVision SLA"
                    options={platformSlaOptions}
                    loading={slaOptionsLoading}
                    disabled={readOnly}
                    textFieldProps={{ variant: 'outlined', size: 'small' }}
                  />
                ) : (
                  <TextField
                    label="OneVision SLA"
                    value={slaOptionsLoading ? '' : currentSlaLabel}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled
                  />
                )}
              </Stack>
            ) : null}

            {isNew && !readOnly ? (
              <Button
                startIcon={<DynamicIcon name="trash-2" library="lucide" />}
                variant="text"
                color="error"
                onClick={() => onRemove(index, planId)}
                sx={removePlanButtonSx}
              >
                Remove plan
              </Button>
            ) : null}
          </Stack>
        </PlanAccordionDetails>
      ) : null}
    </PlanAccordionWrapper>
  );
};

export const MemoPlanAccordion = memo(PlanAccordion);
